import { useState } from "react";
import { useMutation, UseMutationResult } from "@tanstack/react-query";
import isEqual from "lodash.isequal";
import { directUploadWithProgress } from "@core/features/uploadFile";
import { updateUserProfile, UpdateUserProfileResponse } from "../utils/updateUserProfile";
import { normalizeUserData } from "../utils/normalizeUserData";
import { ProfileFormData, NormalizedUserData, RawUserData } from "../profile.types";
import { baseProfileSchema } from "../profile.schemas";

export type UseProfileMutationArgs = {
  /** Callback when mutation succeeds */
  onSuccess?: (data: UpdateUserProfileResponse) => void;
  /** Callback to dispatch user update (e.g., to context) */
  onUserUpdate?: (userData: Record<string, unknown>) => void;
  /** User's role */
  userRole: string;
  /** Previous form values (for detecting file changes) */
  previousValues: NormalizedUserData | null;
};

export type UseProfileMutationReturn = UseMutationResult<
  UpdateUserProfileResponse,
  Error,
  ProfileFormData
> & {
  /** The schema for form validation */
  schema: typeof baseProfileSchema;
  /** Default values for the form */
  defaultValues: ProfileFormData;
};

/**
 * Hook for profile update mutation
 * Handles file uploads and profile data updates
 */
export function useProfileMutation({
  onSuccess,
  onUserUpdate,
  userRole,
  previousValues: previousValuesProp,
}: UseProfileMutationArgs): UseProfileMutationReturn {
  const [previousValues, setPreviousValues] = useState(previousValuesProp);

  const mutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      const payload: ProfileFormData & { media_id?: string; resume_media_id?: string } =
        { ...data };

      const { photo, cv } = data;

      // Handle photo upload if changed
      if (photo && !isEqual(photo, previousValues?.photo)) {
        // Check if there's a new file to upload (photo.original is a File)
        if (photo.original instanceof File) {
          const photoUpload = await directUploadWithProgress(
            photo.original,
            { file_type: "profile_photo", alt_text: photo.altText || "" },
            () => {}
          );
          payload.media_id = photoUpload.media_id;
        } else {
          // Only alt text changed, no new file - send alt text directly
          (payload as Record<string, unknown>).photo_alt_text = photo.altText || "";
        }
        delete (payload as Record<string, unknown>).photo;
      }

      // Handle CV upload if changed
      if (cv && !isEqual(cv, previousValues?.cv)) {
        const cvUpload = await directUploadWithProgress(
          cv.original,
          { file_type: "resume" },
          () => {}
        );
        payload.resume_media_id = cvUpload.media_id;
        delete (payload as Record<string, unknown>).cv;
      }

      // Timezone is stored as an array with one element in the form
      if (payload.timezone) {
        (payload as Record<string, unknown>).timezone = payload.timezone[0];
      }

      return updateUserProfile({ data: payload, role: userRole });
    },
    onError: (error: Error) => {
      throw error;
    },
    onSuccess: (data) => {
      const normalizedData = normalizeUserData(data.user_data as RawUserData);
      setPreviousValues(normalizedData);

      // Dispatch user update if callback provided
      onUserUpdate?.(data.user_data);

      // Call success callback
      onSuccess?.(data);
    },
  });

  // Build default values from previous values
  const defaultValues: ProfileFormData = previousValues
    ? {
        firstName: previousValues.firstName ?? "",
        lastName: previousValues.lastName ?? "",
        photo: previousValues.photo,
        pronouns: previousValues.pronouns ?? "",
        website: previousValues.website ?? "",
        bio: previousValues.bio ?? "",
        timezone: previousValues.timezone ? [previousValues.timezone] : [],
        cv: previousValues.cv,
        isEmailNotificationsEnabled: previousValues.isEmailNotificationsEnabled ?? false,
      }
    : {
        firstName: "",
        lastName: "",
        photo: undefined,
        pronouns: "",
        website: "",
        bio: "",
        timezone: [],
        cv: undefined,
        isEmailNotificationsEnabled: false,
      };

  return {
    ...mutation,
    schema: baseProfileSchema,
    defaultValues,
  };
}

export default useProfileMutation;

